import { log } from './logger';
import { describeUnknownError } from './errors';

/**
 * OBS-1 — "Global `error` and `unhandledrejection` handlers write to it."
 *
 * These matter more here than they normally would. The harness runs unattended at a match venue
 * with no devtools attached; an exception that vanishes into the console is an exception Andrew
 * cannot see. So the handlers do two things: write a structured entry (OBS-1) and hand the message
 * to a UI sink so it is visible on the phone (NFR-8 — surfaced, not swallowed).
 */

export type FatalSink = (message: string) => void;

let sink: FatalSink | null = null;
let installed = false;

export function setFatalSink(next: FatalSink | null): void {
  sink = next;
}

function report(kind: string, message: string, detail: Record<string, unknown> | null): void {
  log.error('global', `${kind}: ${message}`, detail ?? undefined);
  if (sink) sink(`${kind}: ${message}`);
}

export function installGlobalHandlers(target: EventTarget = window): () => void {
  if (installed) return () => undefined;
  installed = true;

  const onError = (event: Event) => {
    const e = event as ErrorEvent;
    report('window.error', e.message || 'unknown error', {
      filename: e.filename ?? null,
      lineno: e.lineno ?? null,
      colno: e.colno ?? null,
      error: describeUnknownError(e.error),
    });
  };

  const onRejection = (event: Event) => {
    const e = event as PromiseRejectionEvent;
    const described = describeUnknownError(e.reason);
    report(
      'unhandledrejection',
      typeof described?.['message'] === 'string' ? (described['message'] as string) : String(e.reason),
      described,
    );
  };

  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);

  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
    installed = false;
  };
}
